/**
 * 运营策略详情页自定义Hook
 * 职责: 处理SKU数据加载、保存和品牌列表获取
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormInstance, App } from 'antd';
import api from '../../../services/api';
import type { ProductInfoChangeData } from '../components/SmartCopywriting/types';

export interface Brand {
    id: number;
    name: string;
    tone?: string;
}

export interface SKUData {
    id: number;
    name: string;
    asin?: string;
    sku_code?: string;
    status?: string;
    site?: string;
    category?: string;
    length?: number;
    width?: number;
    height?: number;
    weight?: number;
    purchase_cost_cny?: number;
    cost_price?: number;
    price?: number;
    profit_margin?: number;
    headquarters_shipping_cost_cny?: number;
    fba_fee?: number;
    brand_obj?: number;
    name_cn?: string;
    name_en?: string;
    description?: string;
    site_keywords?: Record<string, string>;
    product_name?: string;
    selling_points?: string[];
    [key: string]: any;
}

interface UseOperationStrategyDetailProps {
    skuId?: string;  // SKU ID from URL params
    form: FormInstance;
}

interface UseOperationStrategyDetailReturn {
    loading: boolean;
    skuData: SKUData | null;
    brands: Brand[];
    activeTab: string;
    setActiveTab: (tab: string) => void;
    handleSave: () => Promise<void>;
    handleCopywritingDataChange: (data: ProductInfoChangeData) => void;
}

/**
 * 将SKU数据转换为表单值
 */
const transformSKUDataToFormValues = (data: SKUData) => ({
    ...data,
    length: data.length ? Number(data.length) : null,
    width: data.width ? Number(data.width) : null,
    height: data.height ? Number(data.height) : null,
    weight: data.weight ? Number(data.weight) : null,
    purchase_cost_cny: data.purchase_cost_cny ? Number(data.purchase_cost_cny) : null,
    cost_price: data.cost_price ? Number(data.cost_price) : null,
    price: data.price ? Number(data.price) : null,
    profit_margin: data.profit_margin !== null && data.profit_margin !== undefined
        ? Number(data.profit_margin)
        : 30.0,
    headquarters_shipping_cost_cny: data.headquarters_shipping_cost_cny
        ? Number(data.headquarters_shipping_cost_cny)
        : null,
    fba_fee: data.fba_fee ? Number(data.fba_fee) : null,
});

export const useOperationStrategyDetail = ({
    skuId,
    form,
}: UseOperationStrategyDetailProps): UseOperationStrategyDetailReturn => {
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [loading, setLoading] = useState(false);
    const [skuData, setSKUData] = useState<SKUData | null>(null);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [activeTab, setActiveTab] = useState('basic');

    // 加载品牌列表
    const fetchBrands = useCallback(async () => {
        try {
            const response = await api.get('/brands/');
            const data = response.data;
            setBrands(data.results || data || []);
        } catch (error) {
            console.error('加载品牌列表失败', error);
        }
    }, []);

    // 加载SKU数据 - 使用ID直接获取
    const loadSKUData = useCallback(async (id: string) => {
        try {
            setLoading(true);
            // 直接通过ID获取SKU，确保精确匹配
            const response = await api.get(`/skus/${id}/`);
            const data = response.data as SKUData;

            setSKUData(data);

            // 填充表单数据
            form.setFieldsValue(transformSKUDataToFormValues(data));

            setTimeout(() => {
                form.validateFields().catch(() => { });
            }, 50);
        } catch (error: any) {
            console.error('加载SKU数据失败:', error);
            if (error.response?.status === 404) {
                message.error('SKU不存在');
            } else {
                message.error('加载数据失败');
            }
            navigate('/operation-strategy');
        } finally {
            setLoading(false);
        }
    }, [form, message, navigate]);

    // 保存SKU数据
    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const idToUpdate = skuData?.id;

            if (!idToUpdate) {
                message.error('无法保存：未找到SKU ID');
                return;
            }

            await api.patch(`/skus/${idToUpdate}/`, values);
            message.success('保存成功');

            // 重新加载数据
            if (skuId) {
                loadSKUData(skuId);
            }
        } catch (error) {
            console.error('保存失败:', error);
            message.error('保存失败，请检查表单');
        } finally {
            setLoading(false);
        }
    }, [form, skuData?.id, message, skuId, loadSKUData]);

    // 处理智能文案组件的数据变更
    const handleCopywritingDataChange = useCallback((data: ProductInfoChangeData) => {
        if (data.product_name !== undefined) {
            form.setFieldValue('product_name', data.product_name);
        }
        if (data.selling_points !== undefined) {
            form.setFieldValue('selling_points', data.selling_points);
        }
        if (data.site_keywords !== undefined) {
            form.setFieldValue('site_keywords', data.site_keywords);
        }
    }, [form]);

    // 初始化加载品牌列表
    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    // 加载SKU数据
    useEffect(() => {
        if (skuId) {
            loadSKUData(skuId);
        }
    }, [skuId, loadSKUData]);

    return {
        loading,
        skuData,
        brands,
        activeTab,
        setActiveTab,
        handleSave,
        handleCopywritingDataChange,
    };
};

export default useOperationStrategyDetail;
